import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, ShoppingBag, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import B2BLayout from "../business/B2BLayout";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const PAYMENT_TERMS = [
    { value: "prepaid", label: "Prepaid (Pay now via Zoho)", desc: "Receive a payment link immediately." },
    { value: "credit_15", label: "Credit · Net 15", desc: "Invoice raised on dispatch, due in 15 days." },
    { value: "credit_30", label: "Credit · Net 30", desc: "Invoice raised on dispatch, due in 30 days." },
];

const BusinessCart = () => {
    const navigate = useNavigate();
    const [cart, setCart] = useState({ items: [], amounts: { subtotal: 0, gst_amount: 0, total: 0, items_count: 0, discount: 0 } });
    const [loading, setLoading] = useState(true);
    const [paymentTerms, setPaymentTerms] = useState("credit_15");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/b2b/cart");
            setCart(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const updateQty = async (productId, qty) => {
        try {
            await api.post("/b2b/cart/items", { product_id: productId, qty });
            await load();
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not update cart");
        }
    };

    const remove = (productId) => updateQty(productId, 0);

    const clear = async () => {
        if (!window.confirm("Clear all items from cart?")) return;
        await api.delete("/b2b/cart");
        await load();
    };

    const submit = async (account) => {
        if (paymentTerms !== "prepaid" && !account?.credit_limit) {
            toast.error("Credit terms not enabled. Choose prepaid or contact your account manager.");
            return;
        }
        if (cart.amounts.total <= 0) {
            toast.error("Cart is empty");
            return;
        }
        try {
            setSubmitting(true);
            const { data } = await api.post("/b2b/orders", { payment_terms: paymentTerms, notes: notes.trim() || null });
            toast.success(`Order ${data.order_no} submitted`);
            navigate(`/business/orders/${data.id}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            if (d?.code === "CREDIT_LIMIT_EXCEEDED") {
                toast.error(`Credit limit exceeded. Outstanding ₹${d.outstanding}, limit ₹${d.limit}.`);
            } else {
                toast.error(typeof d === "string" ? d : d?.message || "Order failed");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <B2BLayout title="Cart & checkout" subtitle="B2B cart">
            {(account) => {
                if (account?.status !== "approved") {
                    return <p className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">Account not approved yet.</p>;
                }
                if (loading) return <div className="h-64 skeleton rounded-3xl" />;

                const items = cart.items || [];
                const credit = Number(account.credit_limit || 0);
                const outstanding = Number(account.current_outstanding || 0);
                const projected = outstanding + (cart.amounts?.total || 0);
                const creditTermsAvailable = credit > 0;
                const wouldExceed = paymentTerms !== "prepaid" && projected > credit;

                return (
                    <div className="grid gap-5 lg:grid-cols-[1fr_360px]" data-testid="b2b-cart">
                        <div className="space-y-3">
                            {items.length === 0 ? (
                                <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center" data-testid="b2b-cart-empty">
                                    <ShoppingBag className="mx-auto h-10 w-10 text-slate-300" />
                                    <p className="mt-2 text-sm text-slate-500">Cart is empty</p>
                                    <Link to="/business/shop" className="btn-brand mt-4 !text-sm">Browse products</Link>
                                </div>
                            ) : (
                                <>
                                    {items.map((it) => (
                                        <div key={it.product_id} className="flex items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-4" data-testid={`b2b-cart-item-${it.product_id}`}>
                                            {it.image && <img src={it.image} alt={it.name} className="h-14 w-14 rounded-lg object-cover" />}
                                            <div className="flex-1 min-w-0">
                                                <p className="line-clamp-1 text-sm font-semibold">{it.name}</p>
                                                <p className="text-xs text-slate-500">{it.brand} · {it.pack_size} · GST {it.gst_percent}%</p>
                                                <p className="mt-0.5 text-xs text-slate-500">
                                                    <span className="font-bold text-brand-700">{inr(it.unit_price)}</span>
                                                    <span className="ml-1 line-through text-slate-400">{inr(it.selling_price)}</span>
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => updateQty(it.product_id, it.qty - 1)} disabled={it.qty <= 1} className="rounded-md border border-zinc-200 p-1.5 hover:bg-zinc-50 disabled:opacity-50" data-testid={`b2b-cart-dec-${it.product_id}`}>−</button>
                                                <span className="w-8 text-center text-sm font-medium" data-testid={`b2b-cart-qty-${it.product_id}`}>{it.qty}</span>
                                                <button onClick={() => updateQty(it.product_id, it.qty + 1)} className="rounded-md border border-zinc-200 p-1.5 hover:bg-zinc-50" data-testid={`b2b-cart-inc-${it.product_id}`}>+</button>
                                            </div>
                                            <p className="w-24 text-right text-sm font-semibold">{inr(it.subtotal)}</p>
                                            <button onClick={() => remove(it.product_id)} className="text-rose-500 hover:text-rose-700" data-testid={`b2b-cart-remove-${it.product_id}`}>
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <button onClick={clear} className="text-xs text-rose-600 hover:underline" data-testid="b2b-cart-clear">Clear cart</button>
                                </>
                            )}
                        </div>

                        {items.length > 0 && (
                            <aside className="rounded-2xl border border-zinc-100 bg-white p-5 shadow-soft" data-testid="b2b-cart-summary">
                                <p className="label-uppercase mb-3">Order summary</p>
                                <dl className="space-y-2 text-sm">
                                    <div className="flex justify-between"><dt className="text-slate-500">Subtotal ({cart.amounts.items_count} items)</dt><dd className="font-medium">{inr(cart.amounts.subtotal)}</dd></div>
                                    {cart.amounts.discount > 0 && <div className="flex justify-between text-emerald-700"><dt>Wholesale discount</dt><dd>− {inr(cart.amounts.discount)}</dd></div>}
                                    <div className="flex justify-between"><dt className="text-slate-500">GST</dt><dd className="font-medium">{inr(cart.amounts.gst_amount)}</dd></div>
                                    {cart.amounts.cgst > 0 && (
                                        <>
                                            <div className="flex justify-between text-xs text-slate-400"><dt>· CGST</dt><dd>{inr(cart.amounts.cgst)}</dd></div>
                                            <div className="flex justify-between text-xs text-slate-400"><dt>· SGST</dt><dd>{inr(cart.amounts.sgst)}</dd></div>
                                        </>
                                    )}
                                    {cart.amounts.igst > 0 && (
                                        <div className="flex justify-between text-xs text-slate-400"><dt>· IGST</dt><dd>{inr(cart.amounts.igst)}</dd></div>
                                    )}
                                    <div className="flex justify-between border-t pt-2 text-base font-bold"><dt>Total</dt><dd>{inr(cart.amounts.total)}</dd></div>
                                </dl>

                                <div className="mt-5">
                                    <p className="label-uppercase mb-2">Payment terms</p>
                                    <div className="space-y-2">
                                        {PAYMENT_TERMS.map((p) => {
                                            const isCredit = p.value !== "prepaid";
                                            const disabled = isCredit && !creditTermsAvailable;
                                            return (
                                                <label
                                                    key={p.value}
                                                    className={`flex cursor-pointer items-start gap-2 rounded-xl border p-2.5 text-xs ${
                                                        paymentTerms === p.value ? "border-brand-300 bg-brand-50" : "border-zinc-200 hover:border-brand-200"
                                                    } ${disabled ? "opacity-50 pointer-events-none" : ""}`}
                                                    data-testid={`b2b-cart-pt-${p.value}`}
                                                >
                                                    <input type="radio" name="pt" value={p.value} checked={paymentTerms === p.value} onChange={() => setPaymentTerms(p.value)} className="mt-0.5" />
                                                    <div>
                                                        <p className="font-medium text-slate-800">{p.label}</p>
                                                        <p className="text-slate-500">{p.desc}</p>
                                                    </div>
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {!creditTermsAvailable && <p className="mt-1 text-xs text-amber-600">Credit not enabled — contact your account manager.</p>}
                                </div>

                                <div className="mt-4">
                                    <p className="label-uppercase mb-1">Notes (optional)</p>
                                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} placeholder="Delivery instructions, batch preferences…" className="w-full rounded-xl border border-zinc-200 p-2 text-xs outline-none focus:border-brand-400" data-testid="b2b-cart-notes" />
                                </div>

                                {wouldExceed && (
                                    <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800" data-testid="b2b-cart-credit-warn">
                                        <p className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3 w-3" /> Credit limit will be exceeded</p>
                                        <p className="mt-1">Outstanding {inr(outstanding)} + Order {inr(cart.amounts.total)} = {inr(projected)} (limit {inr(credit)}).</p>
                                    </div>
                                )}

                                <button
                                    onClick={() => submit(account)}
                                    disabled={submitting || (wouldExceed && paymentTerms !== "prepaid")}
                                    className="btn-brand mt-4 w-full !justify-center !py-3 !text-sm"
                                    data-testid="b2b-cart-submit-btn"
                                >
                                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                                    {submitting ? "Submitting…" : "Place order"}
                                </button>
                            </aside>
                        )}
                    </div>
                );
            }}
        </B2BLayout>
    );
};

export default BusinessCart;
